import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Search, Zap, MessageCircle, LayoutDashboard } from 'lucide-react';
import { ApartmentCard } from '@/components/ApartmentCard';
import { DateRangePicker, type DateRangeValue } from '@/components/DateRangePicker';
import { GuestPicker } from '@/components/GuestPicker';
import { Eyebrow, Photo } from '@/components/ui';
import { HERO_PHOTO_URL } from '@/lib/heroPhoto';
import { apartmentsQuery } from '@/lib/apartments';
import { applySeo } from '@/lib/seo';
import { isPartyComplete, partyToSearch, type PartyValue } from '@/lib/party';
import { captureSearchSubmitted } from '@/lib/analytics';
import { nightsBetween } from '@/lib/booking';
import { m } from '@/paraglide/messages';

// This grid's card width: `max-w-6xl` (1152px) container, `px-6` (24px)
// side padding, `gap-6` (24px) between grid-cols-3 tiles. Above the 1152px
// cap the 3 columns are a fixed 352px each, not a growing viewport fraction.
//   >=1152px (container capped): (1152 - 48 - 48) / 3 = 352
//   1024–1151px (container == 100vw, 3-up): (100vw - 48 - 48) / 3
//   640–1023px (2-up, one gap-6): (100vw - 48 - 24) / 2
//   <640px (1-up): 100vw - 48
const CARD_SIZES =
  '(min-width: 1152px) 352px, (min-width: 1024px) calc((100vw - 96px) / 3), (min-width: 640px) calc((100vw - 72px) / 2), calc(100vw - 48px)';

export const Route = createFileRoute('/')({
  component: HomePage,
});

export function HomePage() {
  const { data, isPending, isError, refetch } = useQuery(apartmentsQuery);

  useEffect(() => {
    return applySeo({
      title: m.home_title(),
      description: m.home_subtitle(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }, []);

  return (
    <main className="flex-1">
      <HeroSection />

      <section className="mx-auto w-full max-w-6xl px-6 pt-16 sm:pt-20">
        <Eyebrow className="mb-2">{m.home_collection_eyebrow()}</Eyebrow>
        <h2 className="mb-8 font-serif text-3xl font-semibold tracking-tight text-[var(--ollio-ink)] sm:text-4xl">
          {m.home_collection_heading()}
        </h2>

        {/* Stable min height so the skeleton → cards/error/empty swap does not
            move the banner + footer (keeps layout shift low). */}
        <div className="min-h-[60vh]">
          {isPending ? (
            <ApartmentGridSkeleton />
          ) : isError ? (
            <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-8 text-center shadow-[var(--shadow-sm)]">
              <p className="mb-4 text-muted">{m.home_error()}</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="inline-flex items-center justify-center rounded-[var(--radius-control)] bg-primary px-4 py-2 font-semibold text-primary-foreground transition hover:opacity-90"
              >
                {m.home_retry()}
              </button>
            </div>
          ) : data.length === 0 ? (
            <p className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-8 text-center text-muted shadow-[var(--shadow-sm)]">
              {m.home_empty()}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {data.map((apt) => (
                <ApartmentCard
                  key={apt.id}
                  apt={apt}
                  fromPriceMinor={apt.from_price_minor}
                  sizes={CARD_SIZES}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <DirectBookingBanner />
    </main>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section className="relative flex min-h-[480px] items-center overflow-hidden sm:min-h-[540px]">
      {/* The hero image, and the LCP element of the whole site. `priority` so
          it is fetched eagerly at high priority rather than lazily — a hero
          that loads last is a hero nobody sees.

          The URL is already a Cloudflare Image Transformation of one of our
          own apartment photos, so it is a real room rather than the branded
          gradient that stood here — see lib/heroPhoto.ts. */}
      <Photo
        src={HERO_PHOTO_URL}
        priority
        sizes="100vw"
        className="absolute inset-0"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{ background: 'linear-gradient(to bottom, rgba(15,23,42,.28), rgba(21,45,69,.80))' }}
      />
      <div className="relative mx-auto w-full max-w-6xl px-6 py-16">
        <div className="mb-8 max-w-2xl text-white sm:mb-10">
          <Eyebrow onDark className="mb-4">
            {m.home_hero_eyebrow()}
          </Eyebrow>
          <h1 className="font-serif text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {m.home_hero_title()}
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-white/85 sm:text-lg">
            {m.home_hero_subtitle()}
          </p>
        </div>
        <HomeSearchBar />
      </div>
    </section>
  );
}

// HomeSearchBar — functional search entry (R4c): collects dates + guest
// composition and navigates to /search. The submit button stays disabled
// until a valid range AND a complete party are chosen (Task 13: a child
// added but not yet given an age must not be searchable — the server would
// have nothing to price tourist tax against). Dates go through the shared
// DateRangePicker (`presentation="field"`): no apartment is picked yet on
// the home page, so it is deliberately availability-agnostic — no
// `dayData`, every rule degrades to "not in the past" (docs/27 §U1). DS
// pill shell around it; GuestPicker takes no `limits` here — no apartment
// is chosen yet, so only "at least one adult" applies (same as its own
// no-limits default).
function HomeSearchBar() {
  const navigate = useNavigate();
  const [range, setRange] = useState<DateRangeValue>({});
  const [party, setParty] = useState<PartyValue>({ adults: 2, childrenAges: [], infants: 0 });

  const canSearch = !!range.checkin && !!range.checkout && isPartyComplete(party);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const { checkin, checkout } = range;
        if (checkin && checkout && isPartyComplete(party)) {
          // First step of the funnel (OH-71). It was never captured, because a
          // comment in lib/analytics.ts still described this form as a disabled
          // placeholder long after it went live.
          captureSearchSubmitted(
            nightsBetween(new Date(checkin), new Date(checkout)),
            party.adults + party.childrenAges.length + party.infants,
          );
          void navigate({ to: '/search', search: { checkin, checkout, ...partyToSearch(party) } });
        }
      }}
      className="flex w-full max-w-2xl flex-col items-stretch gap-1 rounded-3xl border border-[var(--border-hairline)] bg-card p-2 shadow-[var(--shadow-lg)] sm:flex-row sm:items-center sm:rounded-full"
      aria-label="Search apartments"
    >
      {/* "Where" is a statement, not a control: every apartment we let is in
          Belgrade, so there is nothing to choose. It used to render in the same
          weight and colour as the two pickers beside it — and read "Belgrade ·
          Old Town", a neighbourhood the apartments do not actually share — so
          guests took it for an input, clicked it, and got nothing. Muted weight
          is the whole fix: it stops looking like something that would respond. */}
      <SearchField label={m.home_search_where_label()}>
        <div className="mt-0.5 text-sm text-muted">{m.home_search_where_value()}</div>
      </SearchField>
      <span className="hidden h-8 w-px flex-none bg-[var(--border-hairline)] sm:block" aria-hidden="true" />

      {/* DateRangePicker's own trigger already renders the "Dates" label +
          value pair that SearchField provides for the other two fields, so
          it is NOT nested inside a SearchField here — that would duplicate
          the label. flex-1 alone reproduces the same sizing.
          min-w-0 (OH-64): this div is the actual flex item in this row, and
          a flex item's default `min-width: auto` resolves to a CONTENT-based
          minimum — so without this, a long trigger label (e.g. a
          straddling-year range, "30 Dec 2026 – 2 Jan 2027") stops this field
          from shrinking to its fair flex-1 share at all: it grows to fit the
          label instead, squeezing "Where"/"Guests". The trigger's own
          `truncate` span (DateRangePicker.tsx) only gets a chance to clip
          once ITS containing flex item can actually shrink below the
          label's natural width — min-w-0 is what allows that. Confirmed
          empirically: without it, the label's un-clipped width propagates
          straight up to this div regardless of the descendant's own
          overflow-hidden. */}
      <div className="flex-1 min-w-0">
        <DateRangePicker value={range} onChange={setRange} presentation="field" />
      </div>
      <span className="hidden h-8 w-px flex-none bg-[var(--border-hairline)] sm:block" aria-hidden="true" />

      {/* GuestPicker's own trigger already renders the "Guests" label + party
          summary pair, same reasoning as DateRangePicker above — not nested
          inside a SearchField. */}
      <div className="flex-1 min-w-0">
        <GuestPicker value={party} onChange={setParty} />
      </div>

      <button
        type="submit"
        disabled={!canSearch}
        aria-label={m.home_search_cta()}
        className="mt-1 inline-flex h-12 flex-none items-center justify-center gap-2 rounded-full bg-accent px-5 font-semibold text-accent-foreground shadow-[var(--shadow-gold)] transition enabled:hover:bg-[var(--accent-hover)] disabled:cursor-not-allowed disabled:opacity-60 sm:mt-0 sm:w-12 sm:px-0"
      >
        <Search className="h-5 w-5" aria-hidden="true" />
        <span className="sm:hidden">{m.home_search_cta()}</span>
      </button>
    </form>
  );
}

function SearchField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex-1 rounded-2xl px-4 py-2 text-left sm:rounded-full">
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ollio-gold-deep)]">
        {label}
      </div>
      {children}
    </div>
  );
}

// ─── Direct-booking banner ─────────────────────────────────────────────────────
// The three pillars. What they say about the direct channel is deliberately
// checkable rather than promotional: "Up to 12% less" stood here and was a
// direct price comparison with the OTAs, which docs/27 Часть 0 п.1 rules out —
// and it was a claim about price we would then have to keep true on every
// date. Each of these is instead a property of the product that either holds
// or does not.
const DIRECT_FEATURES = [
  { Icon: Zap, label: m.home_direct_feature_confirmation },
  { Icon: MessageCircle, label: m.home_direct_feature_contact },
  { Icon: LayoutDashboard, label: m.home_direct_feature_cabinet },
] as const;

function DirectBookingBanner() {
  return (
    <section className="mx-auto w-full max-w-6xl px-6 pb-16 pt-16 sm:pt-20">
      <div className="flex flex-col gap-8 overflow-hidden rounded-[var(--radius-2xl)] bg-[var(--ollio-navy)] px-8 py-10 text-white sm:px-14 sm:py-12 md:flex-row md:items-center md:gap-10">
        <div className="flex-1">
          <Eyebrow onDark className="mb-3">
            {m.home_direct_eyebrow()}
          </Eyebrow>
          <h3 className="mb-3 font-serif text-2xl font-semibold leading-tight sm:text-3xl">
            {m.home_direct_heading()}
          </h3>
          <p className="max-w-md text-[15px] leading-relaxed text-white/80">
            {m.home_direct_body()}
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-7">
          {DIRECT_FEATURES.map(({ Icon, label }) => (
            <div key={label()} className="max-w-[96px] text-center">
              <div className="mx-auto mb-2.5 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(197,165,90,0.18)]">
                <Icon className="h-[22px] w-[22px] text-[var(--ollio-gold)]" aria-hidden="true" />
              </div>
              <div className="text-[13px] font-semibold text-white/90">{label()}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ApartmentGridSkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
      aria-busy="true"
      aria-label={m.home_loading()}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card"
        >
          <div className="aspect-[4/3] w-full animate-pulse bg-[var(--surface-sunken)]" />
          <div className="space-y-3 p-5">
            <div className="h-5 w-2/3 animate-pulse rounded bg-[var(--surface-sunken)]" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-[var(--surface-sunken)]" />
          </div>
        </div>
      ))}
    </div>
  );
}
